import type { AvatarId } from "@nano/contracts";

/**
 * In-level companion: the player's own character watches from a corner of
 * the puzzle screen as an upper-body bust and reacts to the play. Each
 * archetype has one GPT expression sheet (`companion_<id>_v1`) cut into
 * these emotions, all on one shared canvas so they cross-fade in place.
 */
export const COMPANION_EMOTIONS = ["calm", "happy", "cheer", "thinking", "surprised", "sad", "crying", "sleepy"] as const;
export type CompanionEmotion = (typeof COMPANION_EMOTIONS)[number];

/**
 * Archetypes with a bust sheet. Listed explicitly (not from AVATAR_IDS) so
 * the art pipeline can cut sheets for characters that are still arriving.
 */
export const COMPANION_ARCHETYPES = [
  "wizard",
  "princess",
  "prince",
  "sorceress",
  "angel-boy",
  "angel-girl",
  "demon-boy",
  "demon-girl",
] as const;

export function portraitAssetId(avatarId: AvatarId | string, emotion: CompanionEmotion): string {
  return `portrait.${avatarId}.${emotion}`;
}

/**
 * Where the full-body front sprite would sit behind a bust so that the two
 * heads coincide: body fraction `p` lands on bust fraction `p * a + b`.
 * Worn items and auras are laid out on that virtual body with the same rig
 * as the city avatar, so a hat, glasses, a necklace, wings or a wand land
 * on the bust exactly where they sit on the walking character.
 *
 * Measured from two landmarks (crown of the hair mass and the eye line) on
 * the calm bust against the body's front sockets. Re-measure when either
 * the bust sheet or the body sprite is regenerated.
 */
export interface BustFrame {
  readonly ax: number;
  readonly bx: number;
  readonly ay: number;
  readonly by: number;
}

const BUST_FRAMES: Readonly<Record<string, BustFrame>> = {
  wizard: { ax: 1.0625, bx: -0.0154, ay: 1.5825, by: 0.0173 },
  princess: { ax: 1.2185, bx: -0.1191, ay: 1.9707, by: -0.0119 },
  prince: { ax: 0.9191, bx: 0.0249, ay: 1.6792, by: 0.0132 },
  sorceress: { ax: 1.0752, bx: -0.0676, ay: 1.7049, by: 0.0085 },
  "angel-boy": { ax: 0.9515, bx: 0.0159, ay: 1.355, by: 0.0952 },
  "angel-girl": { ax: 0.9947, bx: -0.0054, ay: 1.563, by: 0.0081 },
  "demon-boy": { ax: 0.9363, bx: 0.0045, ay: 1.4665, by: -0.0026 },
  "demon-girl": { ax: 0.9043, bx: 0.0657, ay: 1.3588, by: 0.0278 },
};

const DEFAULT_FRAME: BustFrame = { ax: 1, bx: 0, ay: 1.6, by: 0 };

export function bustFrame(avatarId: AvatarId | string): BustFrame {
  return BUST_FRAMES[avatarId] ?? DEFAULT_FRAME;
}

/** The virtual body's box in pixels, for a bust drawn at `width` x `height`. */
export function bustBodyBox(avatarId: AvatarId | string, width: number, height: number): { x: number; y: number; width: number; height: number } {
  const f = bustFrame(avatarId);
  return { x: f.bx * width, y: f.by * height, width: f.ax * width, height: f.ay * height };
}
